/**
 * @file businessDays.js
 * @description Pure business-day helpers for pay-date rolling. When an agency
 *   pays "business days only", a pay date that lands on a weekend or US federal
 *   holiday rolls back to the prior business day (how most direct-deposit
 *   payroll behaves — banks don't settle on closed days).
 *
 *   Dates are YYYY-MM-DD local-date strings (no timezone math), consistent with
 *   the rest of the tracker calc engine. US federal holidays are computed with
 *   the standard "observed" shift for fixed-date holidays (Saturday → prior
 *   Friday, Sunday → following Monday), since that's the day banks actually close.
 *
 * @module lib/businessDays
 */

const pad = (n) => String(n).padStart(2, "0");
const keyOf = (y, m1, d) => `${y}-${pad(m1)}-${pad(d)}`;
function parse(dateKey) {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, m - 1, d); // local midnight
}
function toKey(date) {
  return keyOf(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** Nth (1-based) `weekday` (0=Sun…6=Sat) of a 1-based month. */
function nthWeekday(year, month1, weekday, n) {
  const first = new Date(year, month1 - 1, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month1 - 1, 1 + offset + (n - 1) * 7);
}
/** Last `weekday` of a 1-based month. */
function lastWeekday(year, month1, weekday) {
  const last = new Date(year, month1, 0); // day 0 of next month = last day
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month1 - 1, last.getDate() - offset);
}
/** Observed date for a fixed-date holiday: Sat → prior Fri, Sun → next Mon. */
function observedFixed(year, month1, day) {
  const d = new Date(year, month1 - 1, day);
  const dow = d.getDay();
  if (dow === 6) d.setDate(d.getDate() - 1);
  else if (dow === 0) d.setDate(d.getDate() + 1);
  return d;
}

/**
 * US federal holidays (observed) for a year, as a Set of YYYY-MM-DD. Note an
 * observed New Year's can fall on Dec 31 of the prior year.
 */
export function usFederalHolidays(year) {
  const out = [
    observedFixed(year, 1, 1),        // New Year's Day
    nthWeekday(year, 1, 1, 3),        // MLK Jr. — 3rd Mon Jan
    nthWeekday(year, 2, 1, 3),        // Presidents' — 3rd Mon Feb
    lastWeekday(year, 5, 1),          // Memorial — last Mon May
    observedFixed(year, 6, 19),       // Juneteenth
    observedFixed(year, 7, 4),        // Independence Day
    nthWeekday(year, 9, 1, 1),        // Labor — 1st Mon Sep
    nthWeekday(year, 10, 1, 2),       // Columbus — 2nd Mon Oct
    observedFixed(year, 11, 11),      // Veterans Day
    nthWeekday(year, 11, 4, 4),       // Thanksgiving — 4th Thu Nov
    observedFixed(year, 12, 25),      // Christmas
  ];
  return new Set(out.map(toKey));
}

export function isWeekend(dateKey) {
  const dow = parse(dateKey).getDay();
  return dow === 0 || dow === 6;
}

/** True if a weekday and not a US federal holiday. */
export function isBusinessDay(dateKey, holidays) {
  if (isWeekend(dateKey)) return false;
  const y = Number(dateKey.slice(0, 4));
  const hol = holidays || usFederalHolidays(y);
  return !hol.has(dateKey);
}

/**
 * Roll a date back to the prior business day if it falls on a weekend or US
 * federal holiday; otherwise return it unchanged.
 */
export function rollToPriorBusinessDay(dateKey) {
  if (!dateKey) return dateKey;
  const y = Number(dateKey.slice(0, 4));
  // Union neighboring years so a roll-back across Jan 1 / observed Dec 31 works.
  const holidays = new Set([
    ...usFederalHolidays(y - 1),
    ...usFederalHolidays(y),
    ...usFederalHolidays(y + 1),
  ]);
  const d = parse(dateKey);
  let guard = 0;
  while (guard++ < 40) {
    const k = toKey(d);
    if (!isWeekend(k) && !holidays.has(k)) return k;
    d.setDate(d.getDate() - 1);
  }
  return toKey(d);
}
